/**
 * @copyright 2026 Fortee Custom Apparel Inc. All rights reserved.
 * @license PROPRIETARY - Unauthorized copying, modification, or distribution
 *          of this file is strictly prohibited. This software is protected
 *          under the Canadian Copyright Act (R.S.C., 1985, c. C-42).
 * @author Fortee Custom Apparel Inc. <info@fortee.ca>
 *
 * NOTICE: This file contains trade secrets and proprietary information.
 * Reverse engineering, decompilation, or disassembly is prohibited.
 */
/* ​‌​​​‌‌​‍​‌​​‌‌‌‌‍​‌​‌​​‌​‍​‌​‌​‌​​‍​‌​​​‌​‌‍​‌​​​‌​‌‍​​‌​‌‌​‌‍​​‌‌​​‌​‍​​‌‌​​​​‍​​‌‌​​‌​‍​​‌‌​‌‌​‍​​‌​‌‌​‌‍​‌​‌​‌‌​‍​​‌‌​​‌​‍​​‌​‌‌​‌‍​‌​​​​‌​‍​‌​​‌‌​​‍​‌​​​‌​​‍​‌​​‌​‌‌‍​‌​​​‌​‌‍​‌​‌‌​​‌‍​​‌​‌‌​‌‍​​‌‌​​​‌‍​‌‌​​‌​‌‍​​‌‌​‌​​‍​​‌‌​​‌‌‍​​‌‌​‌​​‍​‌‌​​​​‌‍​‌‌​​‌​‌‍​‌‌​​​‌‌‍​​‌‌​​​​‍​​‌‌​​​‌‍​​‌‌​​​‌‍​‌‌​​​‌​ */
/* [zero-width watermark — added by scripts/inject-watermarks.js during build] */
// bundle-cart-builder.js
// Pure-function helper used by the bundle builders (v1 3-step + 2-step)
// to assemble the /cart/add.js items[] array. Extracting this keeps the
// cart payload property keys consistent across builder variants
// (originally introduced in Phase 25 RESEARCH; Phase 25 was reverted but
// this helper was kept since it's still consumed by bundle-builder.js).
//
// Source of contract: assets/fortee-order-schema.js. Since quick task
// 260820-nrm this file OWNS NO KEY NAMES — it adapts the bundle builders'
// context into normalizeOrderItem()'s input and returns what that gives back.
// The single builder adapts into the same input, which is the only reason the
// two can no longer produce two different orders for the same garment.
//
// DO NOT edit this file casually — what it returns is part of the cart-page
// rendering / fulfillment / Edit-Order contract, and is read after checkout by
// a processor that has no way to ask what a key meant.
// Guards: npm run test:order-schema, test:price-integrity, test:cart-mockup.

// optionValueIds is keyed by the option's name AS SERVED to the current locale,
// and a Shopify product OPTION NAME IS TRANSLATABLE — on /fr/ the SP12 beanie
// serves "Couleurs"/"Tailles", so the literal ids['Color'] lookup missed and
// every remote variant resolution returned null. Ask by role instead; the alias
// table lives in forteeShared.optionRole.
function forteeIdsForRole(ids, role) {
  var keys = Object.keys(ids || {});
  for (var i = 0; i < keys.length; i++) {
    if (window.forteeShared.optionRole(keys[i]) === role) return ids[keys[i]];
  }
  return {};
}

(function (root) {
  'use strict';

  // The i18n helper this file used to carry is GONE (quick task 260820-nrm).
  // Every property it built — the Artwork / Original Logo / Mockup / Note keys
  // and the three Logo Color properties — was a translated string on the order,
  // so a /fr/ customer's line arrived as "Illustration Devant (Poitrine
  // gauche)". Nothing in a cart payload is read by a person who chose the
  // storefront language; the cart PAGE translates its labels instead.

  /**
   * Build a single cart-line `properties` object for one (product, color, size, qty) tuple.
   *
   * ADAPTER, NOT A SCHEMA (quick task 260820-nrm). Everything this function
   * knows about the bundle builders is turned into ONE builder-agnostic input
   * and handed to window.forteeOrderSchema.normalizeOrderItem(), which is also
   * what single-builder.js calls. Shaping properties here as well is how the
   * two builders drifted into two different orders for the same garment: this
   * one folded the spot into the key string ("Artwork Front (Left Chest)") and
   * emitted no geometry or identity at all, while the single builder emitted
   * positional `_print_area_N` triples. Neither survived a /fr/ storefront.
   *
   * @param {Object} ctx
   * @param {Object} ctx.product            - The product whose line item this is
   * @param {string} ctx.color              - The color name (matches product.colorHexMap key)
   * @param {string} ctx.size               - The size on this line
   * @param {number} ctx.quantity           - The line quantity
   * @param {Object} ctx.variant            - {id, sku} of the resolved variant
   * @param {Array<string>} ctx.views       - Result of getProductViews(product) — e.g. ['front','back']
   * @param {Object} ctx.store              - Alpine builder store (groupArtwork, decorationGroups, bundleTitle)
   * @param {Object} ctx.bundleData         - {parentHandle, ...}
   * @param {Object} ctx.mockupCdnUrls      - {'<productId>-<color>-<view>': cdnUrl}
   * @param {Object} ctx.notesSnapshot      - {[productId]: 'note'}
   * @param {Object} ctx.placementLabels    - {view: displayName} — RENDERING ONLY, it is translated
   * @param {Object} ctx.placementKeys      - {view: system_alias} — the canonical key
   * @param {Object} ctx.placementMethods   - {view: 'DTF'|'Embroidery'}
   * @param {Object} ctx.placementGeometry  - {view: {wIn, lxIn, lyIn, aspect}} in INCHES
   * @param {string} ctx.artInstructions    - the art team directive for this line
   * @param {Array<string>} ctx.brandColors - brand hexes for this line
   * @returns {Object} cart-line properties object
   */
  function buildCartLineProperties(ctx) {
    var product = ctx.product || {};
    var color = ctx.color;
    var views = ctx.views || [];
    var store = ctx.store || {};
    var bundleData = ctx.bundleData || {};
    var mockupCdnUrls = ctx.mockupCdnUrls || {};
    var notesSnapshot = ctx.notesSnapshot || {};
    // {view: 'Left Chest'} — the human-readable spot. KEPT FOR DISPLAY ONLY.
    // It used to be the source of the cart's placement text, and display_name
    // is on the translation allowlist in scripts/translate-print-areas.py, so a
    // /fr/ order read "Poitrine gauche". It no longer produces any cart key.
    var placementLabels = ctx.placementLabels || {};
    // {view: 'left_chest'} — the print area's `system_alias`, which is
    // denylisted from translation by construction and is therefore the stable
    // key everything else derives from.
    var placementKeys = ctx.placementKeys || {};
    var placementMethods = ctx.placementMethods || {};
    var placementGeometry = ctx.placementGeometry || {};

    var schema = root.forteeOrderSchema;
    if (!schema || typeof schema.normalizeOrderItem !== 'function') {
      // Fail loudly rather than falling back to an ad-hoc shape. A line whose
      // properties do not match the schema is one the processor silently
      // mis-reads, which is worse than an order that did not go through.
      console.error('[FORTEE bundle] fortee-order-schema.js is not loaded — refusing to build a cart line.');
      throw new Error('Order schema unavailable — please reload the page and try again.');
    }

    // Only artwork for groups that apply to THIS product
    var groupArtwork = store.groupArtwork || {};
    var decoGroups = store.decorationGroups || [];

    // …and, when the caller asks for it, only groups this product is actually
    // CHARGED for. placementKeys/placementLabels are built from the product's
    // sel.placements, which is the same list that produces the decoration tier
    // (getDecorationTierName -> the Shopify variant -> the price), so its keys
    // are exactly the paid set.
    //
    // groupArtwork is a separate store and can legitimately hold a logo for a
    // side this product has no selected placement on — step 1 uploads artwork
    // before step 2 picks placements, and _seedDefaultPlacementsFrom cascades
    // the first upload across the default groups. Emitting Artwork for those
    // sends production a decoration nobody paid for.
    //
    // ON BY DEFAULT since quick task 260909-mck. It was opt-in until then, on
    // the reasoning that a caller supplying no paid set would otherwise lose
    // every artwork property. That made "ship a decoration nobody paid for" the
    // behaviour a caller got by FORGETTING a flag, and orders 7498, 7499, 7501,
    // 7503, 7508 and 7603 are what it cost.
    //
    // The two failures are not symmetric, which is what settles the default.
    // Losing an artwork property is loud: the order looks wrong, someone reports
    // it, nothing has been printed. Emitting an unpaid one is silent until
    // production prints a logo the customer was never charged for. Default to
    // the failure that gets caught.
    //
    // The escape hatch survives, but it has to be SAID: restrictToPlacements:
    // false is a decision on the record rather than an omission.
    var restrict = ctx.restrictToPlacements !== false;

    // Restriction with no paid set at all empties the line of artwork. That is
    // the correct trade, but a blank order is not a diagnosis — say it once,
    // loudly, naming the way out.
    if (restrict &&
        !Object.keys(placementKeys).length &&
        !Object.keys(placementLabels).length) {
      console.error('[FORTEE bundle] no paid placement set supplied (placementKeys and ' +
        'placementLabels are both empty) — every artwork property will be dropped for ' +
        'product ' + (product.id || '?') + '. Pass placementKeys, or pass ' +
        'restrictToPlacements:false if this caller genuinely has no paid set.');
    }

    var placements = [];
    decoGroups.forEach(function (group) {
      var appliesToProduct = group.products.some(function (gp) { return gp.id === product.id; });
      if (!appliesToProduct) return;

      var ga = groupArtwork[group.key];
      var hasArtwork = !!(ga && ga.url);
      var paid = placementKeys[group.key] || placementLabels[group.key];

      if (restrict && !paid) {
        if (hasArtwork) {
          console.warn('[FORTEE bundle] artwork on "' + group.key + '" has no selected placement for product ' +
            product.id + ' — omitted from the cart line (it would ship unpriced)');
        }
        return;
      }
      // Without the flag there is no paid set to consult, so artwork is the
      // only evidence the side is decorated at all. A paid placement with NO
      // artwork still counts when the flag IS on: it was charged for, the tier
      // counts it, and "I'll send the file later" is a normal order.
      if (!restrict && !hasArtwork) return;

      var geo = placementGeometry[group.key];
      var sized = !!(geo && isFinite(geo.wIn) && geo.wIn > 0);

      placements.push({
        // group.key is the fallback for callers that supply no paid set (the
        // 3-step v1 builder). canonicalLocation warns on an alias it does not
        // recognise rather than shipping a guessed label in silence.
        key: placementKeys[group.key] || group.key,
        view: group.view || group.key,
        method: placementMethods[group.key],
        originalArtworkUrl: ga && (ga.editedFrom || ga.originalUrl),
        productionArtworkUrl: ga && (ga.bgRemovedUrl || ga.url),
        editsLayerUrl: (ga && ga.editsLayerUrl) || null,
        editsSpec: (ga && ga.editsSpec) || null,
        widthIn: sized ? geo.wIn : null,
        heightIn: sized ? (geo.wIn / (geo.aspect || 1)) : null,
        xIn: sized ? geo.lxIn : null,
        yIn: sized ? geo.lyIn : null,
        // The 2-step captures ONE art directive per line rather than per view,
        // so it rides every placement on the line — the same text the single
        // builder attaches to the view it was typed on.
        artInstructions: ctx.artInstructions || null,
        brandColors: ctx.brandColors || []
      });
    });

    // Per-view mockup CDN URLs, keyed by colour so a line always carries its
    // own garment photo. Exactly one key per view now: the structural
    // `_ftmockup_<view>` and its translated `Mockup <view>` twin held the same
    // URL under two names, and the schema refuses to write a view twice.
    var mockupsByView = {};
    views.forEach(function (view) {
      var cdnUrl = mockupCdnUrls[product.id + '-' + color + '-' + view];
      if (cdnUrl) mockupsByView[view] = cdnUrl;
    });

    var variant = ctx.variant || {};

    // The `Logo Color Preference` / `Logo for Dark Garments` / `Logo for Light
    // Garments` properties are gone (260820-nrm). The single builder dropped
    // them in quick task 260807-lb5; here they were three translated strings
    // with no canonical equivalent — "Adjust per garment" / "Ajuster selon le
    // vêtement" was the same fact spelled two ways. Removal is write-side only:
    // carts that already carry them keep them.
    return schema.normalizeOrderItem({
      builder: 'bundle',
      // Audit 29-06 finding F6 — stamp the submit batch so rollback can tell
      // THIS submit's lines from lines the customer already banked. Without it
      // rollbackPartialCart matched on _bundle_url alone and deleted every line
      // for the bundle product whenever it was added, so a customer who had
      // successfully added the bundle for one team and then hit a partial
      // failure building it for a second team lost BOTH. ctx.submitTimestamp is
      // supplied by the caller so every line in one submit shares a value;
      // Date.now() here would drift across a slow batch.
      submitTimestamp: ctx.submitTimestamp,
      bundle: {
        title: store.bundleTitle || 'Bundle',
        url: '/products/' + (bundleData.parentHandle || '')
      },
      product: { id: product.id, handle: product.handle, name: product.title },
      variant: { id: variant.id, sku: variant.sku },
      color: color,
      size: ctx.size,
      quantity: ctx.quantity,
      note: notesSnapshot[product.id] || null,
      artworkSkipped: !!ctx.artworkSkipped,
      placements: placements,
      mockupsByView: mockupsByView
    });
  }

  // Server-side variant resolution for >250-variant child products. Liquid caps
  // p.variants at 250, so deep Color x Size x Decoration Type combos never reach
  // the JSON bridge. Shopify resolves them via the option_values URL param +
  // Section Rendering API (sections/ftb-variant-json.liquid) — the same endpoint
  // the single builder uses (single-builder.js resolveVariantRemote).
  // Requires product.optionValueIds + product.url from the section's JSON bridge.
  // Resolves to {id, price, available, title} or null. Never rejects.
  function resolveVariantRemote(product, color, size, tierName) {
    try {
      var ids = product && product.optionValueIds;
      if (!ids) return Promise.resolve(null);
      // optionValueIds is keyed by the option's name AS SERVED, and that
      // name is translatable — on /fr/ it arrives as "Couleurs"/"Tailles", so
      // the literal keys missed and every remote variant lookup returned null.
      var a = (forteeIdsForRole(ids, 'color') || {})[color];
      var b = (forteeIdsForRole(ids, 'size') || {})[size];
      if (!a || !b) return Promise.resolve(null);
      // The decoration/tier axis name varies by product ("Decoration Type" on
      // older products, "Decoration" on newer ones). Resolve it by elimination
      // instead of hardcoding, or lookup returns null for "Decoration" products.
      var decoKey = Object.keys(ids).filter(function (k) {
        return !window.forteeShared.optionRole(k);
      })[0];
      var c = (tierName && decoKey) ? (ids[decoKey] || {})[tierName] : null;
      // A PRODUCT WITH A DECORATION AXIS MUST RESOLVE ITS TIER.
      //
      // This used to fall back to `[a, b]` whenever the tier could not be
      // resolved — no tierName, or a name that drifted from the option value.
      // Shopify answers a two-axis request with the FIRST matching variant,
      // which is the cheapest decoration tier, so the fallback quietly sold a
      // two-placement garment at the one-placement price and nothing anywhere
      // said so. Returning null instead surfaces the line through the caller's
      // "these items couldn't be added" path, which names the exact
      // garment/colour/size — a visible failure beats a silent undercharge.
      //
      // Products that genuinely have only Colour x Size have no decoKey at all
      // and still resolve on two axes, exactly as before.
      if (decoKey && !c) return Promise.resolve(null);
      var parts = c ? [a, b, c] : [a, b];
      var base = product.url || ('/products/' + product.handle);
      return fetch(base + '?option_values=' + parts.join(',') + '&sections=ftb-variant-json')
        .then(function (res) { return res.ok ? res.json() : null; })
        .then(function (data) {
          if (!data) return null;
          var m = (data['ftb-variant-json'] || '').match(/data-ftb-variant>\s*(\{[^<]*\})\s*</);
          if (!m) return null;
          var v = JSON.parse(m[1]);
          return v && v.id ? v : null;
        })
        .catch(function (e) {
          console.warn('[FORTEE] remote variant resolve failed:', e);
          return null;
        });
    } catch (e) {
      return Promise.resolve(null);
    }
  }

  root.bundleCartBuilder = {
    buildCartLineProperties: buildCartLineProperties,
    resolveVariantRemote: resolveVariantRemote
  };
})(typeof window !== 'undefined' ? window : this);
